import ax from "axios";
import { jwtDecode } from "jwt-decode";
import _ from "lodash";

import config from "config";
import userAuthUrls from "services/sub-services/user-auth-service/user-auth.urls";
import { PortalAccessProfile } from "types/api";

export const authRoleKey = "athspire_user_role";
export const portalAccessKey = "athspire_portal_access";
export const activeSchoolKey = "athspire_active_school_id";

export const authSessionClearedEvent = "athspire:session-cleared";
export const authRoleClearedEvent = "athspire:role-cleared";
export const authPortalAccessClearedEvent = "athspire:portal-access-cleared";
export const authSessionUpdatedEvent = "athspire:session-updated";

type PersistAuthSessionInput = {
  accessToken?: string | null;
  refreshToken?: string | null;
  role?: string | null;
  portalAccess?: PortalAccessProfile | null;
};

type StoredAuthSession = {
  accessToken: string | null;
  refreshToken: string | null;
  role: string | null;
  portalAccess: PortalAccessProfile | null;
};

const dispatchWindowEvent = (eventName: string) => {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(eventName));
  }
};

export const normalizeUserRole = (role?: string | null) =>
  role?.trim().replace(/\s+/g, "_").toLowerCase() || null;

export const getSessionRefreshDelayMs = (
  accessToken?: string | null,
  now = Date.now(),
  bufferMs = 60_000,
) => {
  if (!accessToken) {
    return null;
  }

  try {
    const { exp } = jwtDecode<{ exp?: number }>(accessToken);

    if (!exp) {
      return null;
    }

    return Math.max(exp * 1000 - now - bufferMs, 0);
  } catch (error) {
    return null;
  }
};

const readStoredPortalAccess = (): PortalAccessProfile | null => {
  const storedPortalAccess = localStorage.getItem(portalAccessKey);

  if (!storedPortalAccess) {
    return null;
  }

  try {
    return JSON.parse(storedPortalAccess);
  } catch (error) {
    localStorage.removeItem(portalAccessKey);
    return null;
  }
};

export const readStoredAuthSession = (): StoredAuthSession => ({
  accessToken: localStorage.getItem(config.accessTokenKey),
  refreshToken: localStorage.getItem(config.refreshTokenKey),
  role: normalizeUserRole(localStorage.getItem(authRoleKey)),
  portalAccess: readStoredPortalAccess(),
});

export const readAuthTokensFromResponse = (
  response: unknown,
  fallbackRefreshToken: string | null = null,
) => ({
  accessToken:
    _.get(response, "data.session.access_token", null) ||
    _.get(response, "data.access_token", null) ||
    _.get(response, "data.token", null),
  refreshToken:
    _.get(response, "data.session.refresh_token", null) ||
    _.get(response, "data.refresh_token", fallbackRefreshToken) ||
    fallbackRefreshToken,
});

export const persistAuthSession = ({
  accessToken,
  refreshToken,
  role,
  portalAccess,
}: PersistAuthSessionInput) => {
  if (accessToken !== undefined) {
    if (accessToken) {
      localStorage.setItem(config.accessTokenKey, accessToken);
    } else {
      localStorage.removeItem(config.accessTokenKey);
    }
  }

  if (refreshToken !== undefined) {
    if (refreshToken) {
      localStorage.setItem(config.refreshTokenKey, refreshToken);
    } else {
      localStorage.removeItem(config.refreshTokenKey);
    }
  }

  if (role !== undefined) {
    const normalizedRole = normalizeUserRole(role);

    if (normalizedRole) {
      localStorage.setItem(authRoleKey, normalizedRole);
    } else {
      localStorage.removeItem(authRoleKey);
    }
  }

  if (portalAccess !== undefined) {
    if (portalAccess) {
      localStorage.setItem(portalAccessKey, JSON.stringify(portalAccess));
    } else {
      localStorage.removeItem(portalAccessKey);
    }
  }

  dispatchWindowEvent(authSessionUpdatedEvent);
};

export const clearAuthSession = ({ clearActiveSchool = true } = {}) => {
  localStorage.removeItem(config.accessTokenKey);
  localStorage.removeItem(config.refreshTokenKey);
  localStorage.removeItem(authRoleKey);
  localStorage.removeItem(portalAccessKey);

  if (clearActiveSchool) {
    localStorage.removeItem(activeSchoolKey);
  }

  dispatchWindowEvent(authSessionClearedEvent);
  dispatchWindowEvent(authRoleClearedEvent);
  dispatchWindowEvent(authPortalAccessClearedEvent);
  dispatchWindowEvent(authSessionUpdatedEvent);
};

export const refreshAuthSession = async () => {
  const currentRefreshToken = localStorage.getItem(config.refreshTokenKey);

  if (!currentRefreshToken) {
    clearAuthSession();
    throw new Error("Missing refresh token");
  }

  try {
    const response = await ax.post(userAuthUrls.refreshSessionUrl(), {
      refresh_token: currentRefreshToken,
    });

    const { accessToken: nextAccessToken, refreshToken: nextRefreshToken } =
      readAuthTokensFromResponse(response, currentRefreshToken);

    if (!nextAccessToken) {
      throw new Error("Failed to refresh tokens.");
    }

    persistAuthSession({
      accessToken: nextAccessToken,
      refreshToken: nextRefreshToken,
    });

    return response;
  } catch (error) {
    clearAuthSession();
    throw error;
  }
};
